/**
 * The transport that executes a generated operation descriptor.
 *
 * `12` §2.4 control 2 forbids a hand-rolled request, and this is not one: nothing here
 * names an endpoint, a path, a query parameter or a response shape. Every one of those
 * comes from `src/api/generated/operations.ts`, which is emitted from the pinned spec.
 * What lives here is the part of an HTTP call that is the same for all 91 operations and
 * that a generator has no opinion about:
 *
 *   * the base URL, injected at build time so a deployment is configuration, not a build;
 *   * `Accept-Language`, so the service's RFC 9457 `title`/`detail` come back in the
 *     planner's locale (G6, G12);
 *   * `Idempotency-Key` on every write, generated per call (G12) — "so neither is a
 *     caller's responsibility to remember";
 *   * `Authorization`, read from `../auth/session` at call time — a bearer token is a
 *     transport concern like the other two, not something each call site re-derives;
 *   * `X-Correlation-Id`, a fresh UUID per call, kept with the operation and the status in a
 *     five-entry in-page log (`./call-log.ts`) so a problem report can name the calls behind it;
 *   * the RFC 9457 parse, so a failure is an `ApiProblemError` and never a bare status;
 *   * cursor pagination (G12), so a caller asks for a collection and not for a page.
 *
 * Splitting it this way is what makes the client regenerable: a bump rewrites the
 * descriptors and cannot touch a transport decision, and the transport has no
 * per-endpoint code that could drift from the contract in the first place.
 */

import type {
  OperationDescriptor,
  ParamsOf,
  ResultOf,
} from './generated/operations';
import { SPEC_VERSION } from './generated/spec';
import { getAccessToken } from '../auth/session';
import { recordCall } from './call-log';
import { reportPageRead } from './walk-progress';
import {
  ApiIncompleteFileError,
  ApiMalformedProblemError,
  ApiProblemError,
  ApiUnreachableError,
  isProblemDetails,
  type ProblemDetails,
} from './problem';

/**
 * Where the API lives.
 *
 * Empty by default, which makes every request same-origin — the deployment G3 assumes,
 * where the front end is served beside the API rather than calling across an origin.
 * `VITE_API_BASE_URL` overrides it for a split deployment and for the e2e run.
 */
export const DEFAULT_BASE_URL: string = import.meta.env['VITE_API_BASE_URL'] ?? '';

/** Sent on every request so a service log can attribute a call to a pinned client. */
export const CLIENT_VERSION_HEADER = 'X-Bata-Client-Spec';

/**
 * Sent on every request, a fresh UUID each time (bata-platform#686). The platform logs it, so a
 * report that carries it leads straight to the log lines of the request — including on a 500 and
 * on a split-origin deployment that would hide a response header from the page.
 */
export const CORRELATION_ID_HEADER = 'X-Correlation-Id';

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface ApiRequestOptions {
  readonly baseUrl?: string;
  /** BCP-47 tag for `Accept-Language`. `useApiQuery` passes the active i18n language. */
  readonly language?: string;
  readonly signal?: AbortSignal;
  /**
   * Idempotency key for a write. Generated when absent, which is the normal path; pass
   * one explicitly to make a retry of the SAME logical write idempotent (G12).
   */
  readonly idempotencyKey?: string;
  /** Injected in tests. Production always uses the platform `fetch`. */
  readonly fetch?: FetchLike;
  /**
   * A screen's own read, not a download's: `collectPages` does not report its pages to a download that is
   * listening (`./walk-progress.ts`, `docs/adr/9074`). The query layer sets it on every walk it makes.
   */
  readonly untracked?: boolean;
}

/** Parameters as the generated descriptors declare them. */
interface CallParams {
  readonly path?: Readonly<Record<string, string | number>>;
  readonly query?: Readonly<Record<string, unknown>>;
  readonly body?: unknown;
}

/**
 * A fresh idempotency key.
 *
 * Exported because `useApiMutation` mints one when a DECISION is composed rather than when
 * a request is sent — see `./hooks.ts`. Two implementations of "a unique key" is one more
 * than the number of places that should be able to get it wrong.
 */
export function newIdempotencyKey(): string {
  const source = globalThis.crypto;
  if (source && typeof source.randomUUID === 'function') return source.randomUUID();
  // jsdom under older Node exposes no WebCrypto. A key only has to be unique per write,
  // and a write that reached a service without one is refused there (400
  // `platform.idempotency_key_required`), so a fallback is better than a throw.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A fresh correlation id: a UUID, which is the shape the platform's log tooling expects.
 *
 * The fallback is for a runtime with no WebCrypto (jsdom under older Node) and still has the
 * 8-4-4-4-12 shape, because a header the service validates must not be the thing that makes a
 * test environment differ from production.
 */
export function newCorrelationId(): string {
  const source = globalThis.crypto;
  if (source && typeof source.randomUUID === 'function') return source.randomUUID();
  const hex = (length: number): string => Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[Math.floor(Math.random() * 4)] ?? '8'}${hex(3)}-${hex(12)}`;
}

/** Builds the URL for one operation from its descriptor and the caller's parameters. */
export function buildUrl(
  operation: OperationDescriptor,
  params: CallParams,
  baseUrl: string = DEFAULT_BASE_URL,
): string {
  let url = operation.path;
  for (const name of operation.pathParams) {
    const value = params.path?.[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(
        `Operation ${operation.operationId} requires path parameter "${name}".`,
      );
    }
    url = url.replace(`{${name}}`, encodeURIComponent(String(value)));
  }

  const search = new URLSearchParams();
  for (const name of operation.queryParams) {
    const value = params.query?.[name];
    // `null` is how the spec spells "unset" on an optional query parameter, and sending
    // `?cursor=null` would be a different request from sending no cursor at all.
    if (value === undefined || value === null) continue;
    // An array is the parameter REPEATED — `articleKey=a&articleKey=b`, which is how the
    // platform's `list[str]` query parameters read it. `String(['a', 'b'])` would send one
    // value, `a,b`, naming an article that does not exist (ADR 9013: the product-image batch
    // read is the first caller to pass an array).
    if (Array.isArray(value)) {
      for (const item of value) search.append(name, String(item));
      continue;
    }
    search.append(name, String(value));
  }
  const queryString = search.toString();
  return `${baseUrl}${url}${queryString ? `?${queryString}` : ''}`;
}

async function readProblem(response: Response): Promise<never> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiMalformedProblemError(response.status);
  }
  if (!isProblemDetails(body)) throw new ApiMalformedProblemError(response.status);
  throw new ApiProblemError(body as ProblemDetails);
}

/**
 * Executes one generated operation.
 *
 * The types come from the descriptor: `params` is exactly what that operation declares
 * and the resolved value is exactly what the spec says it returns. Calling an operation
 * with the wrong parameters is a compile error, which is the property `12` §2.4 wants
 * from "never hand-rolls a request".
 */
export async function callOperation<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions = {},
): Promise<ResultOf<S>> {
  const response = await send(operation, params, options, 'application/json, application/problem+json');
  if (response.status === 204) return undefined as ResultOf<S>;

  try {
    return (await response.json()) as ResultOf<S>;
  } catch {
    throw new ApiMalformedProblemError(response.status);
  }
}

/** What a CSV operation accepts: the platform's CSV of a list (ADR 6194 section 6), and a problem. */
export const CSV_ACCEPT = 'text/csv, application/problem+json';

/**
 * Executes one generated operation whose answer is a CSV, handed over whole as text — the platform's rows of a list
 * (`GET /exit/consolidation/doors|lines/export`, platform ADR 6194 section 6). The request is
 * `callOperation`'s, header for header (`Authorization`, `Accept-Language`, the correlation id and the call log), and a
 * refusal is the same RFC 9457 `ApiProblemError`; only the body differs. It is ONE request for the whole list: the file is
 * built by `src/export` from these rows, like every other table's.
 */
export async function callOperationCsv<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions = {},
): Promise<string> {
  return (await callOperationCsvFile(operation, params, options)).text;
}

/** The header in which the platform states how many rows a streamed file holds (platform ADR 6201). */
export const ROW_COUNT_HEADER = 'X-Bata-Row-Count';

/** A CSV the platform answered, with the rows it stated it holds — `null` when it stated none. */
export interface CsvFile {
  readonly text: string;
  readonly status: number;
  readonly statedRows: number | null;
}

/**
 * `callOperationCsv`, with the row count the platform states before the first row (`X-Bata-Row-Count`, platform ADR
 * 6201), so the caller that parses the rows can refuse a file that came up short. A body cut before its end is an
 * `ApiIncompleteFileError`; a count that is not a whole number is a malformed answer, never a guess.
 */
export async function callOperationCsvFile<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions = {},
  onProgress?: CsvProgress,
): Promise<CsvFile> {
  const response = await send(operation, params, options, CSV_ACCEPT);
  const statedOf = (): number | null => {
    const stated = response.headers.get(ROW_COUNT_HEADER);
    if (stated === null) return null;
    if (!/^\d+$/.test(stated.trim())) throw new ApiMalformedProblemError(response.status);
    return Number(stated.trim());
  };
  // With a progress callback the count is read first, to say «X of Y» while the body streams.
  const counting = onProgress !== undefined && response.body !== null && response.body !== undefined;
  const early = counting ? statedOf() : null;
  let text: string;
  try {
    text = counting ? await readCounting(response.body!, early, onProgress!) : await response.text();
  } catch {
    // The connection ended before the file did: a short list is never handed over as the whole one.
    throw new ApiIncompleteFileError(response.status);
  }
  const statedRows = counting ? early : statedOf();
  return { text, status: response.status, statedRows };
}

/**
 * A CSV the platform is still sending: its text in decoded pieces, read once, and the rows it stated
 * it holds (`X-Bata-Row-Count`, `null` when it stated none). The pieces are never joined, so a file of
 * any size passes through without ever being one string (a run's cells file ran the tab out of memory
 * at 504,246 rows, 2026-10-23). A body cut before its end throws `ApiIncompleteFileError` from the loop.
 */
export interface CsvStream {
  readonly status: number;
  readonly statedRows: number | null;
  readonly pieces: AsyncIterable<string>;
  /** Stops reading and closes the connection — for a caller that has seen enough. */
  readonly cancel: () => Promise<void>;
}

/** `callOperationCsvFile`, without holding the file: resolves when the headers arrive. */
export async function callOperationCsvStream<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions = {},
): Promise<CsvStream> {
  const response = await send(operation, params, options, CSV_ACCEPT);
  const stated = response.headers.get(ROW_COUNT_HEADER);
  if (stated !== null && !/^\d+$/.test(stated.trim())) throw new ApiMalformedProblemError(response.status);
  const statedRows = stated === null ? null : Number(stated.trim());
  const body = response.body;
  if (body === null || body === undefined) {
    // No stream to read (a test double, an older engine): the text, as one piece.
    let cancelled = false;
    return {
      status: response.status,
      statedRows,
      cancel: async () => { cancelled = true; },
      pieces: (async function* whole() {
        let text: string;
        try { text = await response.text(); } catch { throw new ApiIncompleteFileError(response.status); }
        if (!cancelled) yield text;
      })(),
    };
  }
  const reader = body.getReader();
  return {
    status: response.status,
    statedRows,
    cancel: async () => { await reader.cancel().catch(() => undefined); },
    pieces: (async function* pieces() {
      const decoder = new TextDecoder();
      for (;;) {
        let next: ReadableStreamReadResult<Uint8Array>;
        try {
          next = await reader.read();
        } catch {
          // The connection ended before the file did: a short list is never handed over as the whole one.
          throw new ApiIncompleteFileError(response.status);
        }
        if (next.done) break;
        const piece = decoder.decode(next.value, { stream: true });
        if (piece !== '') yield piece;
      }
      const last = decoder.decode();
      if (last !== '') yield last;
    })(),
  };
}

/**
 * How far a streamed CSV has come: the lines received after the header, and the rows the platform
 * stated (`null` when it stated none). The lines are counted as they arrive, so the figure is a
 * progress indication only — a quoted line break would count twice; the completeness check is the
 * caller's, on the parsed rows.
 */
export type CsvProgress = (received: number, stated: number | null) => void;

async function readCounting(body: ReadableStream<Uint8Array>, stated: number | null, onProgress: CsvProgress): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parts: string[] = [];
  let lines = 0;
  onProgress(0, stated);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    parts.push(chunk);
    for (let at = chunk.indexOf('\n'); at !== -1; at = chunk.indexOf('\n', at + 1)) lines += 1;
    // The first line is the header; never above the stated rows, so the bar does not run past its end.
    const received = Math.max(0, lines - 1);
    onProgress(stated === null ? received : Math.min(received, stated), stated);
  }
  parts.push(decoder.decode());
  return parts.join('');
}

/** The request half of an operation call: the same for a document and for a file. Resolves to an OK response. */
async function send<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions,
  accept: string,
): Promise<Response> {
  const call = (params ?? {}) as CallParams;
  const url = buildUrl(operation, call, options.baseUrl ?? DEFAULT_BASE_URL);
  const perform: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));

  const headers: Record<string, string> = {
    Accept: accept,
    [CLIENT_VERSION_HEADER]: SPEC_VERSION,
  };
  const correlationId = newCorrelationId();
  headers[CORRELATION_ID_HEADER] = correlationId;
  if (options.language !== undefined) headers['Accept-Language'] = options.language;
  if (operation.hasBody) headers['Content-Type'] = 'application/json';
  // G12: "Writes carry an Idempotency-Key." Every non-GET is a write.
  if (operation.method !== 'GET') {
    headers['Idempotency-Key'] = options.idempotencyKey ?? newIdempotencyKey();
  }
  // The transport is the one place a bearer token belongs — the same reasoning that put
  // `Idempotency-Key` here rather than in each call site. A caller with no session sends
  // no header rather than an empty one, so an anonymous request looks like one.
  const token = getAccessToken();
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: operation.method,
    headers,
    ...(operation.hasBody ? { body: JSON.stringify(call.body) } : {}),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  };

  // What the problem report reads back: the id, the operation and what came of it. Recorded as
  // soon as the status is known, before the body is read, so a 500 is on the list too.
  const record = (status: number): void => recordCall({
    correlationId, operationId: operation.operationId, status, at: new Date().toISOString(),
  });

  let response: Response;
  try {
    response = await perform(url, init);
  } catch (cause) {
    // An abort is the caller's own decision, not a failure to report as one.
    if (cause instanceof Error && cause.name === 'AbortError') throw cause;
    record(0);
    throw new ApiUnreachableError(cause);
  }
  record(response.status);

  if (!response.ok) await readProblem(response);
  return response;
}

// ── cursor pagination (G12) ──────────────────────────────────────────────────

/** The page envelope every collection endpoint returns. */
export interface CursorPage<T> {
  readonly items: readonly T[];
  readonly page_size: number;
  readonly has_more?: boolean;
  readonly next_cursor?: string | null;
}

/**
 * A safety rail, not a page size.
 *
 * A caller asking for "all of it" must not be able to walk a collection for ever if a
 * service ever returns a cursor that does not advance. Exceeding it is a thrown error
 * rather than a silent truncation, because a silently short list is a wrong number on a
 * planner's screen and G2 has one rule about those.
 */
export const MAX_PAGES = 50;

export class PaginationLimitError extends Error {
  constructor(operationId: string) {
    super(
      `Operation ${operationId} returned more than ${MAX_PAGES} pages. Either the perimeter `
      + 'needs narrowing or the cursor is not advancing; both are bugs rather than large results.',
    );
    this.name = 'PaginationLimitError';
  }
}

/**
 * The page size `collectPages` asks for when its caller named none: the platform's
 * `maximum_page_size` default (bata-platform `services/api/config.py`).
 *
 * Without it every walk ran at the service's DEFAULT page size, 100 rows, so `MAX_PAGES`
 * capped "all of it" at 5,000 rows — and the first real Colombia pull cycle (run
 * `539f868c…`, 2026-09-27) wrote 6,543 proposals: 66 pages, and the allocation screen
 * refused its own run. The rail was right to refuse; the page size was the defect. At
 * 1,000 rows the same walk is 7 requests and the rail stands at 50,000 rows.
 */
export const COLLECTION_PAGE_SIZE = 1000;

/** The platform's refusal of a page size above its configured maximum (G16). */
const PAGE_SIZE_INVALID = 'platform.page_size_invalid';

/** The row type of a collection operation, read off its page envelope. */
export type ItemOf<S> = ResultOf<S> extends CursorPage<infer T> ? T : never;

type PageItem<S> = ItemOf<S>;

/**
 * Follows `next_cursor` to the end of a collection.
 *
 * `cursor` is opaque and is echoed back verbatim — the service issued it, and a client
 * that parses it has coupled itself to a storage detail G12 deliberately hid.
 *
 * **Page size.** A caller that names `limit` gets exactly that. Otherwise, on an operation
 * that takes `limit`, the walk asks for `COLLECTION_PAGE_SIZE`; a deployment whose maximum
 * is lower refuses it with `platform.page_size_invalid` on the FIRST page, and the walk then
 * starts again at the service's own default rather than failing every collection on screen.
 */
export async function collectPages<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: ApiRequestOptions = {},
): Promise<readonly PageItem<S>[]> {
  const base = (params ?? {}) as CallParams;
  const callerLimit = (base.query as Record<string, unknown> | undefined)?.['limit'];
  const pageable = operation.queryParams.includes('limit');
  if (!pageable || (callerLimit !== undefined && callerLimit !== null)) {
    return walkPages(operation, base, options);
  }
  try {
    return await walkPages(operation, { ...base, query: { ...(base.query ?? {}), limit: COLLECTION_PAGE_SIZE } }, options);
  } catch (error) {
    if (error instanceof ApiProblemError && error.code === PAGE_SIZE_INVALID) {
      return walkPages(operation, base, options);
    }
    throw error;
  }
}

async function walkPages<S extends OperationDescriptor>(
  operation: S,
  base: CallParams,
  options: ApiRequestOptions,
): Promise<readonly PageItem<S>[]> {
  const items: PageItem<S>[] = [];
  let cursor: string | null | undefined;
  let pages = 0;

  do {
    const next = {
      ...base,
      query: { ...(base.query ?? {}), ...(cursor === undefined ? {} : { cursor }) },
    } as ParamsOf<S>;

    const page = await callOperation(operation, next, options) as CursorPage<PageItem<S>>;
    items.push(...page.items);
    pages += 1;
    if (options.untracked !== true) reportPageRead(page.items.length);
    cursor = page.has_more === true ? page.next_cursor : null;
    if (pages > MAX_PAGES) throw new PaginationLimitError(operation.operationId);
  } while (cursor !== null && cursor !== undefined);

  return items;
}
